<script setup lang="js">
  import DarkModeSwitcher from "../components/DarkModeSwitcher";
  import logoUrl from "/image/logos/logo.200.png";
  import illustrationUrl from "@/assets/images/illustrations/station.svg";
  import {FormInput, FormLabel, FormTextarea} from "../base-components/Form";
  import Button from "../base-components/Button";
  import LoadingIcon from "../base-components/LoadingIcon";
  import {useFetch} from '../composables/useFetch';
  import {ref} from "vue";
  import {Form} from 'vee-validate';
  import {useUserStore} from "../stores/user.js";
  import {useRoute, useRouter} from "vue-router";
  import {uri} from "@/constants/config.js";
  import Lucide from "@/base-components/Lucide/index.ts";
  import InputText from "@/base-components/Form/InputText.vue";
  import useValidate from "@/composables/useValidate.js";
  import VOtpInput from "vue3-otp-input";
  import {useI18n} from "vue-i18n";
  import {object} from "yup";
  import {toast} from "vue3-toastify";


  const {t: $t} = useI18n()
  const route = useRoute()
  const router = useRouter()
  const user = useUserStore()
  const form = ref({
    email: null,
    password: null
  })
  const localLoginStatus = ref(true);
  const otpLoginStatus = ref(false);
  const googleLoginStatus = ref(false);
  const localRegisterStatus = ref(false);
  const loadingFlag = ref(false);
  const errorMessage = ref('');
  const fields = {
    email: {required: true, min: 3},
    password: {required: true, min: 8, pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/},
  };

  const successMessage = ref('');
  const login = async () => {
    loadingFlag.value = true;
    successMessage.value = '';
    errorMessage.value = '';
    try {
      // Await the result of useFetch
      const {data, error} = await useFetch(uri.api.user.login, {body: form.value});
      if (error) {
        errorMessage.value = error.message || 'An error occurred while logging in.';
      } else {
        successMessage.value = 'Login successful!';
        user.setUser(data)
        const redirectPath = router.currentRoute.value.query.redirect || '/';
        await router.push(redirectPath);
      }
    } catch (err) {
      errorMessage.value = err.message || 'An unexpected error occurred.';
    } finally {
      loadingFlag.value = false;
    }
  };

  const mobile = ref(null)
  const otp = ref(null)
  const showOpt = ref(false)
  const requestOtpSchema = Object(useValidate(['mobile']))
  const verifyOtpSchema = Object(useValidate([]))
  const showRequestOtpFormFlag = ref(false);
  const showVerifyOtpFormFlag = ref(false);
  const isOtpInvalid = ref(false)
  const verifyBtn = ref(false)
  const hasTime = ref(true)
  const requestParams = ref({
    email: null,
    password: null,
    password_confirm: null,
  })
  const defaultRequestParams = ref({
    email: null,
    password: null,
    password_confirm: null,
  })
  const validationSchemaRegister = object(useValidate( [ 'email', 'password', 'password_confirm'] ),)
  const togglePasswordViewFlag = ref(true)
  const toggleRetypePasswordViewFlag = ref(true)

  function togglePasswordView() {
    togglePasswordViewFlag.value = !togglePasswordViewFlag.value
  }

  function toggleRetypePasswordView() {
    toggleRetypePasswordViewFlag.value = !toggleRetypePasswordViewFlag.value
  }

  const requestOtp = async () => {
    loadingFlag.value = true;
    successMessage.value = '';
    errorMessage.value = '';
    try {
      // Await the result of useFetch
      const {
        data,
        error,
        response,
        statusCode
      } = await useFetch(uri.api.auth.otp.request, {body: {mobile: mobile.value}});
      if (error) {
        errorMessage.value = error.message || 'An error occurred while logging in.';
        if(statusCode && statusCode === 429){
          showVerifyOtpForm()
        }
      } else {
        showOpt.value = true
        showRequestOtpFormFlag.value = false
        showVerifyOtpFormFlag.value = true
        countdown()
      }
    } catch (err) {
      errorMessage.value = err.message || 'An unexpected error occurred.';
    } finally {
      loadingFlag.value = false;
    }
  };
  const verifyOtp = async () => {
    loadingFlag.value = true;
    successMessage.value = '';
    errorMessage.value = '';
    try {
      // Await the result of useFetch
      const {data, error, response, statusCode} = await useFetch(uri.api.auth.otp.request, {
        body: {
          mobile: mobile.value,
          code: otp.value,
        }
      });
      if (error) {
        errorMessage.value = error.message || 'An error occurred while logging in.';
      } else {
        successMessage.value = 'Login successful!';
        user.setUser(data)
        const redirectPath = router.currentRoute.value.query.redirect || '/';
        await router.push(redirectPath);
      }
    } catch (err) {
      errorMessage.value = err.message || 'An unexpected error occurred.';
    } finally {
      loadingFlag.value = false;
    }
  };

  function handleOnComplete(value) {
    otp.value = value
  }

  function handleOnChange(value) {
    isOtpInvalid.value = false
    if (value && value.length === 6) {
      verifyBtn.value = true
    } else {
      verifyBtn.value = false
    }
  }

  function countdown() {
    clearInterval(interval.value)
    interval.value = setInterval(() => {
      timer.value = timer.value.split(':');
      let minutes = timer.value[0];
      let seconds = timer.value[1];
      seconds -= 1;
      if (minutes < 0) return;
      else if (seconds < 0 && minutes !== 0) {
        minutes -= 1;
        seconds = 59;
      } else if (seconds < 10 && seconds.length !== 2) seconds = '0' + seconds;
      timer.value = (minutes + ':' + seconds);
      if (minutes === 0 && seconds === 0) {
        hasTime.value = false
        clearInterval(interval.value);
      }
    }, 1000);
  }

  function clickOtpLogin() {
    if (!showRequestOtpFormFlag.value && (!showVerifyOtpFormFlag.value)) {
      localLoginStatus.value = false;
      localRegisterStatus.value = false;
      otpLoginStatus.value = true;
      showVerifyOtpFormFlag.value = false;
      showRequestOtpFormFlag.value = true
    }
  }

  function clickLoginLocal() {
    localLoginStatus.value = true;
    localRegisterStatus.value = false;
    otpLoginStatus.value = false;
    showRequestOtpFormFlag.value = false;
    showVerifyOtpFormFlag.value = false
  }
  function clickRegisterLocal() {
    localRegisterStatus.value = true;
    localLoginStatus.value = false;
    otpLoginStatus.value = false;
    showRequestOtpFormFlag.value = false;
    showVerifyOtpFormFlag.value = false
  }

  async function localRegister() {
    loadingFlag.value = true;
    successMessage.value = '';
    errorMessage.value = '';
    try {
      // Await the result of useFetch
      const {data, error} = await useFetch(uri.api.user.register, {body: requestParams.value});
      if (error) {
        errorMessage.value = error.message || 'An error occurred while logging in.';
      } else {
        successMessage.value = 'Login successful!';
        clickLoginLocal()
        Object.assign(requestParams.value,defaultRequestParams.value)
        toast($t('message.register-successfully'), {
          theme: "auto",
          type: "success",
          dangerouslyHTMLString: false,
        });
      }
    } catch (err) {
      errorMessage.value = err.message || 'An unexpected error occurred.';
    } finally {
      loadingFlag.value = false;
    }
  }

  const onGoogleClick = () => {
    const url = new URL(window.location.href)
    url.search = ''
    url.pathname = '/auth/callback'
    window.location.href = `${uri.api.auth.google}?callbackURL=${encodeURIComponent(url.toString())}`
  }

  const showVerifyOtpForm = ()=>{
      localLoginStatus.value = false;
      localRegisterStatus.value = false;
      otpLoginStatus.value = true;
      showVerifyOtpFormFlag.value = true;
      showRequestOtpFormFlag.value = false

  }
</script>

<template>
  <div
    :class="[
      '-m-3 sm:-mx-8 p-3 sm:px-8 relative h-screen lg:overflow-hidden bg-primary xl:bg-white dark:bg-darkmode-800 xl:dark:bg-darkmode-600',
      'before:hidden before:xl:block before:content-[\'\'] before:w-[57%] before:-mt-[28%] before:-mb-[16%] before:-mr-[13%] before:absolute before:inset-y-0 before:right-0 before:transform before:rotate-[-4.5deg] before:bg-primary/20 before:rounded-[100%] before:dark:bg-darkmode-400',
      'after:hidden after:xl:block after:content-[\'\'] after:w-[57%] after:-mt-[20%] after:-mb-[13%] after:-mr-[13%] after:absolute after:inset-y-0 after:right-0 after:transform after:rotate-[-4.5deg] after:bg-primary after:rounded-[100%] after:dark:bg-darkmode-700',
    ]"
  >
    <!--    <DarkModeSwitcher/>-->
    <!--    <MainColorSwitcher />-->
    <div class="container relative z-10 sm:px-10">
      <div class="block grid-cols-2 gap-4 xl:grid">
        <!-- BEGIN: Login Info -->
        <div class="flex-col hidden min-h-screen xl:flex">
          <a href="" class="flex items-center pt-5 -intro-x">
            <img
              alt="Midone Tailwind HTML Admin Template"
              class="w-14"
              :src="logoUrl"
            />
            <span class="ml-3 text-lg text-white"> DezhCloud </span>
          </a>
          <div class="my-auto">
            <img
              alt="Midone Tailwind HTML Admin Template"
              class="w-4/5 -mt-16 -intro-x"
              :src="illustrationUrl"
            />
            <div
              class="mt-10 text-4xl font-medium leading-tight text-white -intro-x"
            >
              {{ $t("title.web-title") }}
            </div>
            <div
              class="mt-5 text-lg text-white -intro-x text-opacity-70 dark:text-slate-400"
              v-html="$t('text.web-bio').replace(/\n/g, '<br>')"
            />
          </div>
        </div>
        <!-- END: Login Info -->
        <!-- BEGIN: Login Form -->
        <div class="flex h-screen py-5 my-10 xl:h-auto xl:py-0 xl:my-0">
          <div
            class="w-full px-5 pb-8 mx-auto my-auto bg-white rounded-md shadow-md xl:ml-20 dark:bg-darkmode-600 xl:bg-transparent sm:px-8 xl:p-0 xl:shadow-none sm:w-3/4 lg:w-2/4 xl:w-full"
          >
            <div
              class="w-full my-4 border-b border-gray-200 grid grid-cols-4 gap-4 py-2"
            >
              <Button
                v-if="localLoginStatus"
                variant="primary"
                class="w-full text-xs align-top gap-2"
                type="submit"
                size="sm"
                @click="clickLoginLocal"
              >
                <Lucide icon="Lock" class="block mx-auto" />
                {{ $t("button.local-login") }}
              </Button>
              <Button
                v-else
                variant="outline-primary"
                class="w-full text-xs align-top gap-2"
                type="submit"
                size="sm"
                @click="clickLoginLocal"
              >
                <Lucide icon="Lock" class="block mx-auto" />
                {{ $t("button.local-login") }}
              </Button>

              <Button
                v-if="otpLoginStatus"
                variant="primary"
                class="w-full text-xs align-top gap-2"
                type="submit"
                size="sm"
                @click="clickOtpLogin"
              >
                <Lucide icon="MessageCircle" class="block mx-auto" />
                {{ $t("button.otp-login") }}
              </Button>
              <Button
                v-else
                variant="outline-primary"
                class="w-full text-xs align-top gap-2"
                type="submit"
                size="sm"
                @click="clickOtpLogin"
              >
                <Lucide icon="MessageCircle" class="block mx-auto" />
                {{ $t("button.otp-login") }}
              </Button>

              <Button
                v-if="localRegisterStatus"
                variant="primary"
                class="w-full text-xs align-top gap-2"
                type="submit"
                size="sm"
                @click="clickRegisterLocal"
              >
                <Lucide icon="UserPlus" class="block mx-auto" />
                {{ $t("button.register") }}
              </Button>
              <Button
                v-else
                variant="outline-primary"
                class="w-full text-xs align-top gap-2"
                type="submit"
                size="sm"
                @click="clickRegisterLocal"
              >
                <Lucide icon="UserPlus" class="block mx-auto" />
                {{ $t("button.register") }}
              </Button>

              <Button
                @click="onGoogleClick"
                variant="outline-primary"
                class="w-full text-xs align-top gap-2"
                type="submit"
                size="sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  x="0px"
                  y="0px"
                  width="20"
                  height="20"
                  viewBox="0 0 48 48"
                >
                  <path
                    fill="#fbc02d"
                    d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12	s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20	s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                  ></path>
                  <path
                    fill="#e53935"
                    d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039	l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                  ></path>
                  <path
                    fill="#4caf50"
                    d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36	c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                  ></path>
                  <path
                    fill="#1565c0"
                    d="M43.611,20.083L43.595,20L42,20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571	c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                  ></path>
                </svg>
                {{ $t("button.google-login") }}
              </Button>
            </div>

            <div class="mt-2 text-center intro-x text-slate-400 xl:hidden">
              {{ $t("text.web-bio") }}
            </div>

            <div v-if="otpLoginStatus">
              <Form
                v-if="showRequestOtpFormFlag"
                @submit="requestOtp"
                :validation-schema="requestOtpSchema"
              >
                <div class="mt-8 intro-x mb-20" dir="ltr">
                  <InputText
                    type="text"
                    name="mobile"
                    :disabled="loadingFlag"
                    class="w-full"
                    :placeholder="$t('input.mobile')"
                    v-model="mobile"
                  />
                </div>
                <div class="mt-5 text-center intro-x xl:mt-8 xl:text-left">
                  <Button
                    v-if="!loadingFlag"
                    variant="dark"
                    class="w-full px-2 py-1 align-top xl:w-32 xl:mr-3"
                    type="submit"
                    size="sm"
                  >
                    {{ $t("button.request-otp") }}
                    <Lucide
                      icon="ArrowLeft"
                      class="block me-0 float-left h-4"
                    />
                  </Button>
                  <free-style-shimmer
                    v-else
                    width="100%"
                    height="25px"
                    class="w-full"
                  />
                </div>
              </Form>
              <Form
                v-if="showVerifyOtpFormFlag"
                @submit="verifyOtp"
                :validation-schema="verifyOtpSchema"
              >
                <div class="mt-8 intro-x mb-20" dir="ltr">
                  <VOtpInput
                    ref="otpInput"
                    class="w-full mx-auto"
                    input-classes="px-2 mx-1 w-10 rounded-md border-gray-200 border border-gray-300
                       shadow-sm sm:text-sm py-3 text-center outline-1 outline-blue-500"
                    separator=""
                    :num-inputs="6"
                    :should-auto-focus="true"
                    :is-input-num="true"
                    inputmode="numeric"
                    @on-change="handleOnChange"
                    @on-complete="handleOnComplete"
                  />
                </div>
                <div class="mt-5 text-center intro-x xl:mt-8 xl:text-left">
                  <div class="mt-5 text-center intro-x xl:mt-8 xl:text-left">
                    <Button
                      v-if="!loadingFlag"
                      variant="dark"
                      class="w-full px-2 py-1 align-top xl:w-32 xl:mr-3"
                      type="submit"
                      size="sm"
                    >
                      {{ $t("button.verify-otp") }}
                      <Lucide
                        icon="ArrowLeft"
                        class="block me-0 float-left h-4"
                      />
                    </Button>
                    <free-style-shimmer
                      v-else
                      width="100%"
                      height="25px"
                      class="w-full"
                    />
                  </div>
                </div>
              </Form>
            </div>

            <div v-if="localLoginStatus">
              <Form @submit="login">
                <div class="mt-8 intro-x" dir="ltr">
                  <FormInput
                    type="text"
                    class="block px-4 py-3 intro-x login__input min-w-full xl:min-w-[350px]"
                    :placeholder="$t('input.username')"
                    v-model="form.email"
                  />
                  <FormInput
                    type="password"
                    class="block px-4 py-3 mt-4 intro-x login__input min-w-full xl:min-w-[350px]"
                    :placeholder="$t('input.password')"
                    v-model="form.password"
                  />
                </div>
                <div class="mt-5 text-center intro-x xl:mt-8 xl:text-left">
                  <Button
                    v-if="!loadingFlag"
                    variant="dark"
                    class="w-full px-2 py-1 align-top xl:w-32 xl:mr-3"
                    type="submit"
                    size="sm"
                  >
                    {{ $t("button.login") }}
                    <Lucide
                      icon="ArrowLeft"
                      class="block me-0 float-left h-4"
                    />
                  </Button>
                  <free-style-shimmer
                    v-else
                    width="100%"
                    height="25px"
                    class="w-full"
                  />
                </div>
              </Form>
            </div>

            <div v-if="localRegisterStatus">
              <Form
                class="w-full"
                :validation-schema="validationSchemaRegister"
                @submit="localRegister"
              >
                <input type="password" class="hidden" />
                <input type="text" name="username" class="hidden" />
                <input type="text" name="mobile" class="hidden" />
                <input type="text" name="email" class="hidden" />
                <div class="px-2 min-h-72">
                  <div class="grid grid-cols-12 gap-4 mt-5 gap-y-5">
                    <div class="col-span-12 intro-y">
                      <InputText
                        name="email"
                        :dir="'ltr'"
                        :has-label="true"
                        :label="$t('label.email')"
                        v-model="requestParams.email"
                        id="input-wizard-3"
                        type="text"
                      />
                    </div>

                    <div class="col-span-12 intro-y">
                      <div class="relative flex mx-auto">
                        <InputText
                          class="w-full pe-9"
                          :dir="'ltr'"
                          :has-label="true"
                          :label="$t('label.password')"
                          name="password"
                          id="input-wizard-3"
                          v-model="requestParams.password"
                          :type="togglePasswordViewFlag ? 'password' : 'text'"
                        />
                        <div
                          class="cursor-pointer flex absolute bottom-0 end-0 items-center h-[41px] justify-center w-10 border rounded-l bg-slate-100 text-slate-500 dark:bg-darkmode-700 dark:border-darkmode-800 dark:text-slate-400"
                          @click="togglePasswordView"
                        >
                          <Lucide
                            :icon="togglePasswordViewFlag ? 'Eye' : 'EyeOff'"
                            class="w-6 h-5 text-black"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="col-span-12 intro-y">
                      <div class="relative flex mx-auto">
                        <InputText
                          class="w-full pe-9"
                          :dir="'ltr'"
                          :has-label="true"
                          :label="$t('label.retype-password')"
                          name="password_confirm"
                          id="input-wizard-3"
                          v-model="requestParams.password_confirm"
                          :type="
                            toggleRetypePasswordViewFlag ? 'password' : 'text'
                          "
                        />
                        <div
                          class="cursor-pointer flex absolute bottom-0 end-0 items-center h-[41px] justify-center w-10 border rounded-l bg-slate-100 text-slate-500 dark:bg-darkmode-700 dark:border-darkmode-800 dark:text-slate-400"
                          @click="toggleRetypePasswordView"
                        >
                          <Lucide
                            :icon="
                              toggleRetypePasswordViewFlag ? 'Eye' : 'EyeOff'
                            "
                            class="w-6 h-5 text-black"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-5 intro-x xl:mt-8 text-end">
                  <Button
                    v-if="!loadingFlag"
                    variant="dark"
                    class="px-2 py-1 align-top xl:w-32 xl:mr-3"
                    type="submit"
                    size="sm"
                  >
                    {{ $t("button.register") }}
                  </Button>
                  <free-style-shimmer
                    v-else
                    width="100%"
                    height="25px"
                    class="w-full"
                  />
                </div>
              </Form>
            </div>
          </div>
        </div>
        <!-- END: Login Form -->
      </div>
    </div>
  </div>
</template>
